import { getRouteApi } from "@tanstack/react-router";
import { Client, FamilyDetail } from "creso-core/types";
import {
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useClientsByIds } from "creso-core/hooks";
import { useClientStatusCards } from "@crm/components/status/client-status";
import { StackedStatusCards } from "@crm/components/status/stacked-status-cards";

// ACTIVE/INACTIVE/UPDATE_* only show a card in narrower cases — not worth
// an extra fetch for every family member here.
const NEEDS_ATTENTION_STATUSES = [
  "LEAD",
  "VERIFICATION_PENDING",
  "SETUP_PENDING",
  "ACTIVE",
  "UPDATE_PENDING",
  "UPDATE_UNDER_REVIEW",
  "UNDER_REVIEW",
];

function cardKeys(cards: ReactNode[]) {
  return cards
    .map((card) => (isValidElement(card) ? card.key : null))
    .join(",");
}

// Headless: computes one client's cards and reports them up, rendering
// nothing itself. Stays mounted even when empty so its data can still
// resolve later.
function FamilyMemberStatusReporter({
  client,
  onCardsChange,
}: {
  client: Client;
  onCardsChange: (clientId: number, cards: ReactNode[]) => void;
}) {
  const cards = useClientStatusCards(client, {
    name: client.name,
    holdingPattern: client.holding_pattern,
  });
  const cardsSignature = cardKeys(cards);

  useEffect(() => {
    onCardsChange(client.id, cards);
    // `cards` changes identity every render; `cardsSignature` is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, cardsSignature, onCardsChange]);

  return null;
}

export function FamilyStatusBar() {
  const route = getRouteApi("/(auth)/_headed/clients/families/$id");
  const { family } = route.useLoaderData() as { family: FamilyDetail };
  const [expanded, setExpanded] = useState(false);
  const [cardsByClientId, setCardsByClientId] = useState<
    Record<number, ReactNode[]>
  >({});

  const alertedClientIds = useMemo(
    () =>
      (family.members ?? [])
        .filter((member) => NEEDS_ATTENTION_STATUSES.includes(member.status))
        .map((member) => member.client_id),
    [family.members]
  );

  const { clients: statusClients } = useClientsByIds(alertedClientIds);

  const handleCardsChange = useCallback(
    (clientId: number, cards: ReactNode[]) => {
      setCardsByClientId((prev) => {
        if (cardKeys(prev[clientId] ?? []) === cardKeys(cards)) return prev;
        return { ...prev, [clientId]: cards };
      });
    },
    []
  );

  if (!statusClients.length) return null;

  const combinedCards = statusClients.flatMap(
    (client) => cardsByClientId[client.id] ?? []
  );

  return (
    <>
      {statusClients.map((client) => (
        <FamilyMemberStatusReporter
          key={client.id}
          client={client}
          onCardsChange={handleCardsChange}
        />
      ))}
      <div
        className="relative w-full"
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
      >
        <StackedStatusCards
          expanded={expanded}
          hoverToExpand={false}
          className="bg-secondary"
        >
          {combinedCards}
        </StackedStatusCards>
      </div>
    </>
  );
}
